import React, { useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import s from './styles/StepTypeApplications.module.scss'
import { ReactComponent as ArrowShortIcon } from 'assets/icons/arrowLeft.svg'
import { ReactComponent as DockIcon } from 'assets/icons/dock.svg'
import { useStepTypeApplications } from './hooks/useStepTypeApplications'
import CheckBox from 'shared/ui/CheckBox/CheckBox'
import LoaderImg from 'shared/components/LoaderImg/LoaderImg'

const LOR_DESIGNATIONS = [
	'Professor',
	'Associate Professor',
	'Assistant Professor',
	'Lecturer',
	'Senior Lecturer',
	'Teaching Assistant',
	'Research Assistant',
	'Research Associate',
	'Postdoctoral Researcher',
	'Department Head',
	'Dean',
]

const COMMON_APP_PROMPTS = [
	'Some students have a background, identity, interest, or talent that is so meaningful they believe their application would be incomplete without it.',
	'The lessons we take from obstacles we encounter can be fundamental to later success. Recount a time when you faced a challenge, setback, or failure.',
	'Reflect on a time when you questioned or challenged a belief or idea. What prompted your thinking? What was the outcome?',
	'Reflect on something that someone has done for you that has made you happy or thankful in a surprising way. How has this gratitude affected or motivated you?',
	'Discuss an accomplishment, event, or realization that sparked a period of personal growth and a new understanding of yourself or others.',
	'Describe a topic, idea, or concept you find so engaging that it makes you lose all track of time. Why does it captivate you?',
	'Share an essay on any topic of your choice. It can be one you have already written, one that responds to a different prompt, or one of your own design.',
]

const lorDesignationSelectStyles = {
	control: (base, state) => ({
		...base,
		minHeight: 58,
		borderRadius: 12,
		borderColor: 'rgba(87, 59, 118, 0.2)',
		boxShadow: state.isFocused ? '0 0 0 1px rgba(87, 59, 118, 0.28)' : 'none',
		fontFamily: "'Satoshi', system-ui, sans-serif",
		fontSize: 16,
		fontWeight: 500,
		cursor: 'pointer',
		'&:hover': { borderColor: 'rgba(87, 59, 118, 0.35)' },
	}),
	valueContainer: base => ({ ...base, padding: '10px 4px 10px 12px' }),
	singleValue: base => ({ ...base, color: '#000', marginLeft: 0 }),
	placeholder: base => ({ ...base, color: 'rgba(0, 0, 0, 0.25)' }),
	input: base => ({ ...base, margin: 0, padding: 0, color: '#000' }),
	indicatorSeparator: () => ({ display: 'none' }),
	dropdownIndicator: base => ({
		...base,
		color: '#573b76',
		paddingRight: 12,
	}),
	menuPortal: base => ({ ...base, zIndex: 1300 }),
	menu: base => ({
		...base,
		borderRadius: 12,
		border: '1px solid rgba(87, 59, 118, 0.2)',
		boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
		overflow: 'hidden',
		marginTop: 6,
		marginBottom: 6,
	}),
	menuList: base => ({
		...base,
		maxHeight: 280,
		padding: 0,
	}),
	option: (base, state) => ({
		...base,
		padding: '12px 14px',
		fontSize: 15,
		fontWeight: 500,
		color: '#101828',
		backgroundColor: state.isSelected
			? 'rgba(87, 59, 118, 0.1)'
			: state.isFocused
				? 'rgba(87, 59, 118, 0.06)'
				: 'transparent',
		cursor: 'pointer',
	}),
}

const UniversitySelect = ({
	options = [],
	selectedTargetId = '',
	onChange = () => {},
	isLoading = false,
	applicationUniversityName = '',
}) => {
	const [open, setOpen] = useState(false)
	const ref = useRef(null)

	useEffect(() => {
		if (!open) return undefined
		const onDown = e => {
			if (ref.current && !ref.current.contains(e.target)) setOpen(false)
		}
		document.addEventListener('mousedown', onDown)
		return () => document.removeEventListener('mousedown', onDown)
	}, [open])

	useEffect(() => {
		setOpen(false)
	}, [selectedTargetId])

	const selectedName =
		options.find(opt => opt.applicationTargetId === selectedTargetId)
			?.universityName ||
		applicationUniversityName ||
		'Select university'

	return (
		<label className={s.field}>
			<span className={s.field__label}>University</span>
			<div className={s.universitySelect} ref={ref}>
				<button
					type="button"
					className={s.universitySelect__trigger}
					onClick={e => {
						e.stopPropagation()
						setOpen(v => !v)
					}}
				>
					<span>{isLoading ? 'Loading universities...' : selectedName}</span>
					<span
						className={[
							s.universitySelect__arrow,
							open ? s.universitySelect__arrowOpen : '',
						].join(' ')}
					>
						<ArrowShortIcon />
					</span>
				</button>
				{open ? (
					<div className={s.universitySelect__menu}>
						{options.length ? (
							options.map(opt => (
								<button
									type="button"
									key={opt.applicationTargetId}
									className={[
										s.universitySelect__item,
										selectedTargetId === opt.applicationTargetId
											? s.active
											: '',
									].join(' ')}
									onClick={e => {
										e.preventDefault()
										e.stopPropagation()
										onChange(opt.applicationTargetId)
										requestAnimationFrame(() => setOpen(false))
									}}
								>
									{opt.universityName}
								</button>
							))
						) : (
							<div className={s.universitySelect__empty}>
								No universities available
							</div>
						)}
					</div>
				) : null}
			</div>
		</label>
	)
}

const StepTypeApplications = ({
	assignmentType = 'Document',
	onSelect = () => {},
	missingItems = [],
	uploadedItems = [],
	onDocumentTargetsChange = () => {},
	essayPrompt = '',
	onEssayPromptChange = () => {},
	essayWordLimit = '',
	onEssayWordLimitChange = () => {},
	essayMode = 'university',
	onEssayModeChange = () => {},
	lockedSelection = false,
	resetKey = 0,
	lorRecommenderName = '',
	onLorRecommenderNameChange = () => {},
	lorProfessorDesignation = '',
	onLorProfessorDesignationChange = () => {},
	applicationUniversityName = '',
	hasApplicationTargetContext = false,
	essayUniversityOptions = [],
	selectedEssayUniversityTargetId = '',
	onEssayUniversityChange = () => {},
	isEssayUniversityLoading = false,
	documentsLoading = false,
}) => {
	const lorDesignationOptions = useMemo(
		() => LOR_DESIGNATIONS.map(v => ({ value: v, label: v })),
		[],
	)
	const lorDesignationValue = useMemo(
		() =>
			lorProfessorDesignation
				? { value: lorProfessorDesignation, label: lorProfessorDesignation }
				: null,
		[lorProfessorDesignation],
	)
	const commonAppPromptOptions = useMemo(
		() =>
			COMMON_APP_PROMPTS.map((prompt, index) => ({
				value: prompt,
				label: `Prompt ${index + 1}`,
				prompt,
			})),
		[],
	)
	const selectedCommonAppPrompt = useMemo(
		() =>
			commonAppPromptOptions.find(option => option.value === essayPrompt) || null,
		[commonAppPromptOptions, essayPrompt],
	)

	const {
		open,
		dropdownRef,
		assignmentTypes,
		toggleDropdown,
		selectAssignmentType,
		isSelectedAll,
		onToggleSelectedAll,
		toggleKey,
		selectedKeys,
		keyM,
		keyU,
	} = useStepTypeApplications({
		onSelect,
		assignmentType,
		missingItems,
		uploadedItems,
		onDocumentTargetsChange,
		lockedSelection,
		resetKey,
	})

	return (
		<div className={s.root}>
			<div className={s.head}>
				<div className={s.head__info}>
					<div className={s.head__icon}>
						<DockIcon />
					</div>
					<span className={s.head__title}>Assignment Type</span>
				</div>
				<div className={s.dropdown} ref={dropdownRef}>
					<button
						type="button"
						className={s.dropdown__trigger}
						onClick={toggleDropdown}
					>
						<span>{assignmentType || '—'}</span>
						<span
							className={[
								s.dropdown__arrow,
								open ? s.dropdown__arrowOpen : '',
							].join(' ')}
						>
							<ArrowShortIcon />
						</span>
					</button>
					{open && (
						<div className={s.dropdown__menu}>
							{assignmentTypes.map(type => (
								<button
									type="button"
									key={type}
									className={[
										s.dropdown__item,
										assignmentType === type ? s.active : '',
									].join(' ')}
									onClick={() => selectAssignmentType(type)}
								>
									{type}
								</button>
							))}
						</div>
					)}
				</div>
			</div>

			{assignmentType === 'LoR' && (
				<div className={s.lorCard}>
					<h3 className={s.lorTitle}>Add recommendation letter</h3>
					<p className={s.lorSubtitle}>
						Track an LoR for the student&apos;s{' '}
						{applicationUniversityName
							? `${applicationUniversityName} application`
							: 'application'}
					</p>

					{!hasApplicationTargetContext && (
						<div className={s.lorField}>
							<UniversitySelect
								options={essayUniversityOptions}
								selectedTargetId={selectedEssayUniversityTargetId}
								onChange={onEssayUniversityChange}
								isLoading={isEssayUniversityLoading}
								applicationUniversityName={applicationUniversityName}
							/>
						</div>
					)}

					<div className={s.lorField}>
						<label className={s.lorLabel} htmlFor="lor-recommender-name">
							Recommender name
						</label>
						<input
							id="lor-recommender-name"
							className={s.lorInput}
							type="text"
							placeholder="e.g. Prof Anand Sharma"
							value={lorRecommenderName}
							onChange={e => onLorRecommenderNameChange?.(e.target.value)}
						/>
					</div>

					<div className={s.lorField}>
						<span className={s.lorLabel} id="lor-professor-designation-label">
							Professor designation
						</span>
						<div className={s.lorSelectRs}>
							<Select
								instanceId="lor-professor-designation"
								inputId="lor-professor-designation-input"
								aria-labelledby="lor-professor-designation-label"
								options={lorDesignationOptions}
								value={lorDesignationValue}
								onChange={opt =>
									onLorProfessorDesignationChange?.(opt?.value || '')
								}
								placeholder="Select role"
								isSearchable={false}
								isClearable={false}
								styles={lorDesignationSelectStyles}
								menuPlacement="auto"
								menuPosition="fixed"
								menuPortalTarget={
									typeof document !== 'undefined' ? document.body : null
								}
								menuShouldBlockScroll
							/>
						</div>
					</div>
				</div>
			)}

			{assignmentType === 'Essay' && (
				<div className={s.essayCard}>
					<div className={s.essayCard__head}>
						<h3 className={s.essayCard__title}>Assign Essay</h3>
						<p className={s.essayCard__subtitle}>
							Student will see this essay title in their portal
						</p>
					</div>
					<div className={s.essayModeSwitch}>
						<button
							type="button"
							className={[
								s.essayModeSwitch__item,
								essayMode === 'university' ? s.active : '',
							].join(' ')}
							onClick={() => onEssayModeChange?.('university')}
						>
							University Essay
						</button>
						<button
							type="button"
							className={[
								s.essayModeSwitch__item,
								essayMode === 'common_app' ? s.active : '',
							].join(' ')}
							onClick={() => onEssayModeChange?.('common_app')}
						>
							Common App Essay
						</button>
					</div>

					<div className={s.fieldWrap}>
						{essayMode !== 'common_app' && !hasApplicationTargetContext && (
							<UniversitySelect
								options={essayUniversityOptions}
								selectedTargetId={selectedEssayUniversityTargetId}
								onChange={onEssayUniversityChange}
								isLoading={isEssayUniversityLoading}
								applicationUniversityName={applicationUniversityName}
							/>
						)}
						<div className={s.fieldRow}>
							<label className={s.field}>
								<span className={s.field__label}>Word Limit</span>
								<input
									className={s.field__input}
									type="text"
									inputMode="numeric"
									pattern="[0-9]*"
									placeholder="e.g., 500"
									value={essayWordLimit}
									onChange={e =>
										onEssayWordLimitChange?.(
											String(e.target.value || '').replace(/[^\d]/g, ''),
										)
									}
								/>
							</label>
						</div>
					</div>

					{essayMode === 'common_app' ? (
						<div className={s.field}>
							<span className={s.field__label}>Common App Prompt</span>
							<div className={s.promptSelectRs}>
								<Select
									instanceId="essay-common-app-prompt"
									inputId="essay-common-app-prompt-input"
									options={commonAppPromptOptions}
									value={selectedCommonAppPrompt}
									onChange={option =>
										onEssayPromptChange?.(option?.value || '')
									}
									isSearchable={false}
									isClearable={false}
									placeholder="Select common app prompt"
									getOptionLabel={option =>
										option?.prompt
											? `${option.label} — ${option.prompt}`
											: option.label
									}
									styles={lorDesignationSelectStyles}
									menuPlacement="auto"
									menuPosition="fixed"
									menuPortalTarget={
										typeof document !== 'undefined' ? document.body : null
									}
									menuShouldBlockScroll
								/>
							</div>
						</div>
					) : (
						<label className={s.field}>
							<span className={s.field__label}>Essay prompt</span>
							<textarea
								className={s.field__textarea}
								placeholder="e.g., Explain your interest in computer science"
								value={essayPrompt}
								onChange={e => onEssayPromptChange?.(e.target.value)}
								rows={5}
							/>
						</label>
					)}
				</div>
			)}

			{assignmentType === 'Document' && documentsLoading ? (
				<div className={s.documentsLoading}>
					<LoaderImg />
					<span>Loading documents...</span>
				</div>
			) : null}

			{assignmentType === 'Document' && !documentsLoading && (
				<>
					{!hasApplicationTargetContext && (
						<div className={s.docUniversity}>
							<UniversitySelect
								options={essayUniversityOptions}
								selectedTargetId={selectedEssayUniversityTargetId}
								onChange={onEssayUniversityChange}
								isLoading={isEssayUniversityLoading}
								applicationUniversityName={applicationUniversityName}
							/>
						</div>
					)}
					<div className={s.warn}>
						<p className={s.warn__text}>
							{missingItems.length > 0 ? (
								<>
									{missingItems.length} document
									{missingItems.length === 1 ? '' : 's'} need uploading. Select
									which ones to include in this assignment.
								</>
							) : uploadedItems.length > 0 ? (
								<>
									Request a new version for uploaded documents. Select which
									items to include in this assignment.
								</>
							) : (
								<>No documents available for this assignment.</>
							)}
						</p>
						<div
							className={s.action}
							style={
								lockedSelection
									? { opacity: 0.5, pointerEvents: 'none' }
									: undefined
							}
						>
							<span
								className={s.action__text}
								role="button"
								tabIndex={lockedSelection ? -1 : 0}
								onClick={lockedSelection ? undefined : onToggleSelectedAll}
								onKeyDown={
									lockedSelection
										? undefined
										: e => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault()
													onToggleSelectedAll()
												}
											}
								}
							>
								Select All
							</span>
							<CheckBox
								isSelected={isSelectedAll}
								onToggle={onToggleSelectedAll}
								className={s.action__check}
								classNameLabel={s.action__label}
								variant="square"
							/>
						</div>
					</div>

					<div className={s.items}>
						{missingItems.map(item => (
							<div
								className={s.item}
								key={item.id}
								onClick={() => toggleKey(keyM(item.id))}
							>
								<div className={s.item__inner}>
									<div className={s.item__info}>
										<div className={s.item__icon}>
											<DockIcon />
										</div>
										<span className={s.item__title}>{item.title}</span>
									</div>
									<span className={s.item__status}>Missing</span>
								</div>
								<CheckBox
									isSelected={selectedKeys.has(keyM(item.id))}
									className={s.item__check}
									onToggle={() => {}}
									variant="square"
								/>
							</div>
						))}
					</div>

					{uploadedItems.length > 0 && (
						<>
							<p className={s.uploadedHint}>
								Already uploaded — select to request a new version (re-upload)
								in the same assignment.
							</p>
							<div className={s.items}>
								{uploadedItems.map(item => (
									<div className={s.item} key={item.id}>
										<div
											className={s.item__inner}
											role="button"
											tabIndex={lockedSelection ? -1 : 0}
											onClick={
												lockedSelection
													? undefined
													: () => toggleKey(keyU(item.id))
											}
											onKeyDown={
												lockedSelection
													? undefined
													: e => {
															if (e.key === 'Enter' || e.key === ' ') {
																e.preventDefault()
																toggleKey(keyU(item.id))
															}
														}
											}
										>
											<div className={s.item__icon}>
												<DockIcon />
											</div>
											<span className={s.item__title}>{item.title}</span>
											<span
												className={`${s.item__status} ${s.item__statusUploaded}`}
											>
												Uploaded
											</span>
										</div>
										<CheckBox
											isSelected={selectedKeys.has(keyU(item.id))}
											onToggle={
												lockedSelection
													? () => {}
													: () => toggleKey(keyU(item.id))
											}
											className={s.action__check}
											classNameLabel={s.action__label}
											variant="square"
										/>
									</div>
								))}
							</div>
						</>
					)}
				</>
			)}
		</div>
	)
}

export default StepTypeApplications
